import type { ParseOptions, SubtitleCaption, SubtitleFormat } from "./types";
import { toMilliseconds } from "./utils/time";

/**
 * Parses SRT or WebVTT subtitle content into an array of caption objects
 */
export const parse = (content: string, options: ParseOptions = {}): SubtitleCaption[] => {
  if (typeof content !== "string") {
    throw new Error("Content must be a string");
  }

  const { format, normalizeLineEndings = true } = options;

  // Normalize line endings
  let normalizedContent = content;
  if (normalizeLineEndings) {
    normalizedContent = content.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  }

  // Detect format if not specified
  const detectedFormat = format || detectFormat(normalizedContent);

  switch (detectedFormat) {
    case "srt":
      return parseSrt(normalizedContent);
    case "vtt":
      return parseVtt(normalizedContent);
    default:
      throw new Error(`Unsupported subtitle format: ${detectedFormat}`);
  }
};

/**
 * Detects the subtitle format based on content
 */
const detectFormat = (content: string): SubtitleFormat => {
  const trimmed = content.trim();

  // Check for VTT header
  if (trimmed.startsWith("WEBVTT")) {
    return "vtt";
  }

  // Check for SRT format (number followed by timestamp)
  const lines = trimmed.split("\n");
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine && /^\d+$/.test(trimmedLine)) {
      return "srt";
    }
  }

  // Default to SRT if we can't determine
  return "srt";
};

/**
 * Parses SRT format content
 */
const parseSrt = (content: string): SubtitleCaption[] => {
  const captions: SubtitleCaption[] = [];
  const blocks = content.split(/\n\s*\n/).filter((block) => block.trim());

  for (const block of blocks) {
    const lines = block.split("\n").filter((line) => line.trim());
    if (lines.length < 3) continue;

    // Skip if first line is not a number
    if (!/^\d+$/.test(lines[0]!.trim())) continue;

    // Parse timestamp line
    const timestampLine = lines[1]!.trim();
    const timestampMatch = timestampLine.match(
      /^(\d{1,2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{1,2}:\d{2}:\d{2},\d{3})$/
    );

    if (!timestampMatch) continue;

    const startTime = toMilliseconds(timestampMatch[1]!);
    const endTime = toMilliseconds(timestampMatch[2]!);

    // Get text content (everything after timestamp line)
    const text = lines.slice(2).join("\n").trim();

    if (text) {
      captions.push({
        start: startTime,
        end: endTime,
        text,
      });
    }
  }

  return captions;
};

/**
 * Parses WebVTT format content
 */
const parseVtt = (content: string): SubtitleCaption[] => {
  const captions: SubtitleCaption[] = [];
  const lines = content.split("\n");

  // Skip header
  let i = 0;
  while (i < lines.length && !lines[i]!.trim().startsWith("WEBVTT")) {
    i++;
  }
  i++; // Skip WEBVTT line

  // Skip metadata
  while (i < lines.length && lines[i]!.trim() && !lines[i]!.includes("-->")) {
    i++;
  }

  // Parse caption blocks
  while (i < lines.length) {
    // Skip empty lines
    while (i < lines.length && !lines[i]!.trim()) {
      i++;
    }

    if (i >= lines.length) break;

    // Parse timestamp line
    const timestampLine = lines[i]!.trim();
    const timestampMatch = timestampLine.match(
      /^(\d{1,2}:\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{1,2}:\d{2}:\d{2}\.\d{3})(?:\s+(.+))?$/
    );

    if (!timestampMatch) {
      i++;
      continue;
    }

    const startTime = toMilliseconds(timestampMatch[1]!);
    const endTime = toMilliseconds(timestampMatch[2]!);
    const settings = timestampMatch[3];

    i++;

    // Collect text lines
    const textLines: string[] = [];
    while (i < lines.length && lines[i]!.trim()) {
      textLines.push(lines[i]!.trim());
      i++;
    }

    const text = textLines.join("\n");

    if (text) {
      const caption: SubtitleCaption = {
        start: startTime,
        end: endTime,
        text,
      };

      if (settings) {
        caption.settings = settings;
      }

      captions.push(caption);
    }
  }

  return captions;
};
